/**
 * Cart Count Updater
 * Event-driven cart count sync with debounced API checks.
 */

(function() {
  'use strict';

  var HEADER_COUNT_SELECTOR = '.header__cart-count';
  var EMPTY_CLASS = 'header__cart-count--empty';
  var HAS_ITEMS_CLASS = 'header__cart-count--has-items';
  var MIN_SYNC_INTERVAL = 700;

  var syncTimer = null;
  var syncInFlight = false;
  var lastSyncAt = 0;
  var lastKnownCount = null;

  function parseCount(value) {
    var count = parseInt(value, 10);
    return Number.isNaN(count) ? 0 : count;
  }

  function getCountElements() {
    return Array.prototype.slice.call(document.querySelectorAll(HEADER_COUNT_SELECTOR));
  }

  function readCurrentCount() {
    var firstElement = getCountElements()[0];
    if (!firstElement) return 0;
    return parseCount(firstElement.textContent.trim());
  }

  function updateCountClasses(element, itemCount) {
    var empty = itemCount === 0;
    element.classList.remove(EMPTY_CLASS, HAS_ITEMS_CLASS);
    element.classList.add(empty ? EMPTY_CLASS : HAS_ITEMS_CLASS);
    // .ff-basket-count--empty is the class the theme CSS actually uses to hide the badge.
    element.classList.toggle('ff-basket-count--empty', empty);
    // Reflect the empty / has-items state on the basket button + container too.
    var toggle = element.closest('.header__cart-toggle');
    if (toggle) {
      toggle.classList.toggle('ff-basket-btn', !empty);
      toggle.classList.toggle('ff-basket-empty', empty);
      toggle.classList.toggle('ff-icon-btn', empty);
    }
    var item = element.closest('.header__action-item--cart');
    if (item) item.classList.toggle('ff-basket-has-items', !empty);
  }

  function dispatchCartUpdated(itemCount, source) {
    document.documentElement.dispatchEvent(new CustomEvent('ff:cart-updated', {
      bubbles: true,
      detail: {
        itemCount: itemCount,
        source: source || 'unknown'
      }
    }));
  }

  function renderCount(itemCount, source) {
    // Whenever we know the live count, reconcile the SlideCart backdrop lock (see below).
    neutraliseSlidecartLock(itemCount === 0);

    var countElements = getCountElements();
    if (!countElements.length) return;

    countElements.forEach(function(element) {
      if (element.textContent.trim() !== String(itemCount)) {
        element.textContent = itemCount;
      }
      updateCountClasses(element, itemCount);
    });

    // Open-on-add — unified for EVERY add path (normal PDP form, compatibility checker, etc.)
    // and decoupled from AMP's unreliable SLIDECART_ADD_TO_CART callback: a genuine count
    // increase means an item went in, so open the drawer. Skips the first paint.
    if (lastKnownCount !== null && itemCount > lastKnownCount && source !== 'init:dom') {
      openCartOnAdd();
    }

    lastKnownCount = itemCount;
    dispatchCartUpdated(itemCount, source);
  }

  // The single open-on-add path for the whole theme. Uses SLIDECART_UPDATE (a reliable
  // METHOD, unlike the add callbacks) so the drawer refetches its contents before opening —
  // no stale/empty flash. Debounced so an optimistic count bump plus its confirming /cart.js
  // sync don't double-fire.
  var lastOpenAt = 0;
  function openCartOnAdd() {
    var now = Date.now();
    if (now - lastOpenAt < 1200) return;
    lastOpenAt = now;
    try {
      if (typeof window.SLIDECART_UPDATE === 'function') {
        window.SLIDECART_UPDATE(function() {
          if (typeof window.SLIDECART_OPEN === 'function') { window.SLIDECART_OPEN(); }
        });
      }
      // Fallback: SLIDECART_UPDATE's callback can be unreliable, so open directly a beat later
      // too. A second OPEN is a harmless no-op if already open.
      setTimeout(function() {
        if (typeof window.SLIDECART_OPEN === 'function') { window.SLIDECART_OPEN(); }
      }, 450);
    } catch (e) {}
    // NOTE: opening does not work immediately after the cart has been fully emptied — AMP's
    // OPEN method (and its native toggle path) are left in a stuck state by its buggy
    // empty-close. This is an accepted minor edge case (empty the whole cart, then re-add
    // without a reload); every other add path opens correctly.
  }

  function syncCountFromApi(source) {
    if (syncInFlight || typeof window.fetch !== 'function') return;
    if ((Date.now() - lastSyncAt) < MIN_SYNC_INTERVAL) return;

    lastSyncAt = Date.now();
    syncInFlight = true;

    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function(response) {
        if (!response.ok) throw new Error('Unable to fetch cart');
        return response.json();
      })
      .then(function(cart) {
        renderCount(parseCount(cart && cart.item_count), source || 'cart.js');
      })
      .catch(function() {
        renderCount(readCurrentCount(), 'dom-fallback');
      })
      .finally(function() {
        syncInFlight = false;
      });
  }

  function scheduleSync(source, delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function() {
      syncCountFromApi(source);
    }, typeof delay === 'number' ? delay : 150);
  }

  // Cart-mutating actions in SlideCart / the compatibility checker run in app code we
  // can't hook (their fetches are made in a different JS realm). A DOM click, however,
  // always reaches us — so on those clicks we poll /cart.js a few times to absorb the
  // app's server round-trip and reflect the true count on the badge.
  function pollCartAfterAction(source) {
    [400, 1000, 2000].forEach(function(delay) {
      setTimeout(function() {
        lastSyncAt = 0;
        syncCountFromApi(source);
      }, delay);
    });
  }

  function handleProductAdded(event) {
    var quantity = parseCount(event && event.detail && event.detail.quantity);
    if (quantity < 1) quantity = 1;

    var currentCount = lastKnownCount === null ? readCurrentCount() : lastKnownCount;
    renderCount(currentCount + quantity, 'product:added');
    scheduleSync('product:added', 500);
  }

  function handleCartEvent(event) {
    var eventName = (event && event.type) || 'cart:event';
    if (eventName === 'cart:updated' && event && event.detail && typeof event.detail.itemCount !== 'undefined') {
      renderCount(parseCount(event.detail.itemCount), eventName);
      return;
    }

    scheduleSync(eventName, 140);
  }

  function installUiActionHints() {
    document.addEventListener('click', function(event) {
      var target = event.target;
      if (!target) return;

      if (
        target.closest('#slidecarthq') ||
        target.closest('[data-ff-compat-add]') ||
        target.closest('.compatibility-checker__button') ||
        target.closest('[data-action="remove-item"]') ||
        target.closest('.item-remove') ||
        target.closest('.cart__remove-button') ||
        target.closest('[data-cart-remove]') ||
        target.closest('a[href*="/cart/change"]') ||
        target.closest('[name="plus"]') ||
        target.closest('[name="minus"]')
      ) {
        pollCartAfterAction('cart:ui-click');
      }
    }, true);

    document.addEventListener('change', function(event) {
      var target = event.target;
      if (!target) return;

      if (
        target.matches('input[name="updates[]"]') ||
        target.matches('input[name="quantity"]') ||
        target.matches('input.quantity-selector__value') ||
        target.matches('[data-quantity-input]')
      ) {
        scheduleSync('cart:ui-change', 320);
      }
    }, true);
  }

  // SlideCart HQ doesn't fire theme cart events, but it exposes global callbacks
  // (https://slide-cart.helpscoutdocs.com/article/87). SLIDECART_UPDATED fires on ANY
  // cart change and hands us the cart object, so we read item_count straight off it.
  // We chain any pre-existing callback so we don't clobber another integration.
  function registerSlideCartCallbacks() {
    function applyCart(cart) {
      var count = (cart && typeof cart.item_count !== 'undefined') ? parseCount(cart.item_count) : null;

      // Update the badge — renderCount() also handles open-on-add (via count increase) and
      // the empty-cart backdrop neutraliser.
      if (count !== null) {
        renderCount(count, 'slidecart');
      }
      // ...then confirm against the committed cart (the snapshot can be stale on
      // quantity changes, which is why multiples weren't reflecting).
      scheduleSync('slidecart', 200);
    }

    var prevUpdated = window.SLIDECART_UPDATED;
    window.SLIDECART_UPDATED = function(cart) {
      if (typeof prevUpdated === 'function') { try { prevUpdated(cart); } catch (e) {} }
      applyCart(cart);
    };
    var prevLoaded = window.SLIDECART_LOADED;
    window.SLIDECART_LOADED = function(cart) {
      if (typeof prevLoaded === 'function') { try { prevLoaded(cart); } catch (e) {} }
      applyCart(cart);
    };
    // Open-on-add is handled centrally in renderCount() (any genuine count increase), so we
    // no longer hook SLIDECART_ADD_TO_CART — that callback fired unreliably and only on some
    // add paths anyway.
    // SlideCart doesn't always release its scroll-lock when the drawer auto-closes after
    // an item is removed — the page stays frozen on mobile until a manual refresh. Hook
    // the documented CLOSED callback and defensively restore scrollability.
    var prevClosed = window.SLIDECART_CLOSED;
    window.SLIDECART_CLOSED = function() {
      if (typeof prevClosed === 'function') { try { prevClosed(); } catch (e) {} }
      enforceUnlock(700);
    };
    // The freeze is triggered specifically by REMOVING an item (the drawer then auto-closes).
    // Hook the removal callback too and enforce the unlock across the close animation —
    // belt-and-braces in case CLOSED doesn't fire reliably on that path.
    var prevRemoved = window.SLIDECART_REMOVED_FROM_CART;
    window.SLIDECART_REMOVED_FROM_CART = function(payload) {
      if (typeof prevRemoved === 'function') { try { prevRemoved(payload); } catch (e) {} }
      enforceUnlock(1800);
    };
  }

  // Re-assert the unlock repeatedly across a short window — the app can re-touch the lock
  // during its close animation, so a single pass gets undone. Cheap and self-stopping; it
  // only runs after a close/remove, so it can't fight a genuinely-open drawer for long.
  function enforceUnlock(ms) {
    restoreScrollLock();
    var until = Date.now() + (ms || 800);
    var iv = setInterval(function() {
      restoreScrollLock();
      if (Date.now() > until) clearInterval(iv);
    }, 120);
  }

  // THE actual bug (confirmed by on-device inspection): after the last item is removed the
  // cart auto-closes via a path that leaves SlideCart's `.slidecarthq-overlay` backdrop in
  // the DOM — full-screen, max z-index, pointer-events:auto — so it silently swallows every
  // touch and the page can't be scrolled (it was never an overflow lock). When the cart is
  // empty we disable that backdrop (an empty cart's overlay does nothing useful) and clear
  // any stray html overflow lock; when items return we hand control back to SlideCart. This
  // runs off our own /cart.js count, so it works even though AMP fires no close callback.
  function neutraliseSlidecartLock(isEmpty) {
    try {
      var overlays = document.querySelectorAll('.slidecarthq-overlay, [class*="slidecarthq"][class*="overlay"]');
      Array.prototype.forEach.call(overlays, function(o) {
        if (isEmpty) { o.style.setProperty('pointer-events', 'none', 'important'); }
        else { o.style.removeProperty('pointer-events'); }
      });
      var h = document.documentElement;
      if (isEmpty) {
        var cs = window.getComputedStyle(h);
        // Only override a RULE-based lock; leave deliberate inline locks (image-zoom modal,
        // which sets html.style.overflow='hidden' directly) untouched.
        if ((cs.overflowY === 'hidden' || cs.overflow === 'hidden') &&
            h.style.overflow !== 'hidden' && h.style.overflowY !== 'hidden') {
          h.style.setProperty('overflow-y', 'auto', 'important');
        }
      } else if (h.style.getPropertyPriority('overflow-y') === 'important') {
        h.style.removeProperty('overflow-y');
      }
    } catch (e) {}
  }

  // ── AMP scroll-lock leak guard (the real fix) ──────────────────────────────────
  // CONFIRMED on device: after the last item is removed the cart auto-closes via a path that
  // leaves AMP's body-scroll-lock `touchmove` preventDefault listener attached to `document`
  // (taps still work, but the page can't be scrolled). AMP exposes no teardown API
  // (clearAllBodyScrollLocks is undefined), so we can't remove their listener — we neutralise
  // it. A capture-phase touchmove handler runs BEFORE their bubble-phase listener and stops
  // the event reaching it, so its preventDefault never fires and native page scrolling works.
  // This does NOT prevent scrolling (native scroll needs no listener) and does NOT affect
  // CSS-based locks (mobile menu/search, image-zoom). We skip elements that legitimately use
  // touchmove (cart drawer, swipe carousels, modals) so they keep working. Over-excluding is
  // safe — it just means the guard doesn't act there; ordinary page content still scrolls.
  var SCROLL_GUARD_EXCLUDE = '#slidecarthq, [class*="slidecart"], .flickity-enabled, ' +
    '.flickity-viewport, .flickity-slider, [data-ff-review-carousel], [class*="carousel"], ' +
    '[class*="drawer"], [class*="modal"], [class*="mobile-nav"], dialog';
  function installScrollUnlockGuard() {
    document.addEventListener('touchmove', function(event) {
      var t = event.target;
      if (t && t.closest && t.closest(SCROLL_GUARD_EXCLUDE)) return;
      event.stopImmediatePropagation();
    }, { capture: true, passive: true });
  }

  // Scroll-lock class names commonly used by drawer apps and the body-scroll-lock lib.
  var LOCK_CLASSES = ['is-locked', 'scroll-lock', 'scroll-locked', 'no-scroll',
    'body-scroll-lock', 'overflow-hidden', 'slidecart-open', 'scart-open',
    'amp-cart-open', 'amp-slide-cart-open', 'cart-open', 'cart-is-open',
    'drawer-open', 'has-cart-open'];

  // Release any lingering page scroll-lock, whatever mechanism left it behind. Amp Slide
  // Cart uses the body-scroll-lock library, which on iOS locks via touchmove preventDefault
  // listeners (not overflow) — so clearing inline styles alone does nothing. We try the
  // library's own teardown first, then strip lock classes and inline style locks.
  function restoreScrollLock() {
    // 1) Library-native teardown — the only clean way to remove iOS touchmove listeners.
    try { if (typeof window.clearAllBodyScrollLocks === 'function') window.clearAllBodyScrollLocks(); } catch (e) {}
    try {
      if (typeof window.enableBodyScroll === 'function') {
        var sc = document.querySelector('#slidecarthq, [class*="slidecart"], [class*="scart"], [class*="slide-cart"]');
        if (sc) window.enableBodyScroll(sc);
      }
    } catch (e) {}

    // 2) Class-based and inline locks on <html> / <body>.
    [document.documentElement, document.body].forEach(function(el) {
      if (!el) return;
      LOCK_CLASSES.forEach(function(c) { el.classList.remove(c); });
      var s = el.style;
      if (s.overflow === 'hidden') s.overflow = '';
      if (s.overflowY === 'hidden') s.overflowY = '';
      if (s.touchAction === 'none') s.touchAction = '';
      if (s.position === 'fixed') {
        var savedTop = s.top;
        s.position = '';
        s.top = '';
        s.left = '';
        s.right = '';
        s.width = '';
        var y = savedTop ? Math.abs(parseInt(savedTop, 10)) : 0;
        if (y) { window.scrollTo(0, y); }
      }
    });
  }

  function init() {
    if (!getCountElements().length) return;

    renderCount(readCurrentCount(), 'init:dom');

    document.addEventListener('product:added', handleProductAdded);
    document.addEventListener('cart:refresh', handleCartEvent);
    document.addEventListener('cart:updated', handleCartEvent);

    window.addEventListener('pageshow', function() {
      scheduleSync('pageshow', 60);
    });

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'visible') {
        scheduleSync('visibilitychange', 80);
      }
    });

    installScrollUnlockGuard();
    installUiActionHints();
    registerSlideCartCallbacks();
    scheduleSync('init:sync', 180);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
